<script setup>
import { ref, computed, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";
import { fetchAuthSession } from "aws-amplify/auth";

const currentStep = ref(1);
const loading = ref(false);
const search = ref("");

const productsArray = ref([]);
const itemArray = ref([]);

const selectedProduct = ref([]);
const selected = ref([]);
const selectedArray = ref([]);

const selectedTypeFilter = ref(["All"]);
const showSelectedOnly = ref(false);

const productObj = ref({
  existingProductId: "",
  productName: "",
  productDescription: "",
});

const resolvedItems = ref({});
const resolving = ref(false);

const showCreationDialog = ref(false);
const showDeleteDialog = ref(false);
const showDeleteSuccessDialog = ref(false);
const showDeleteFailedDialog = ref(false);
const showWarningDialog = ref(false);
const showSubmissionDialog = ref(false);
const showCompletionDialog = ref(false);
const showCompletionWarningDialog = ref(false);

const objectTypeDisplayNames = {
  policies: "Policies",
  scripts: "Scripts",
  packages: "Packages",
  computergroups: "Computer Groups",
  mobiledevicegroups: "Mobile Groups",
  osxconfigurationprofiles: "macOS Configuration Profiles",
  mobiledeviceconfigurationprofiles: "Mobile Configuration Profiles",
  computerextensionattributes: "macOS Extension Attributes",
  mobiledeviceextensionattributes: "Mobile Extension Attributes",
  categories: "Categories",
  dockitems: "Dock Items",
  diskencryptionconfigurations: "Disk Encryption Configurations",
  restrictedsoftware: "Restricted Software",
  advancedcomputersearches: "Advanced Computer Searches",
  advancedmobiledevicesearches: "Advanced Mobile Searches",
  networksegments: "Network Segments",
  "/v3/computer-prestages": "Computer Prestages",
  "/v2/mobile-device-prestages": "Mobile Device Prestages",
};

const steps = [
  { title: "Select Product", value: 1, complete: false },
  { title: "Select Templates", value: 2, complete: false },
  { title: "Review & Save", value: 3, complete: false },
];

const productHeaders = [
  { title: "Name", value: "name", sortable: true },
  { title: "Description", value: "description", sortable: false },
  {
    title: "Created/Last Edited By",
    value: "latestEditor",
    sortable: true,
    width: "30%",
  },
];

const itemHeaders = [
  { title: "Name", value: "name", sortable: true, width: "50%" },
  { title: "Type", value: "typeDisplay", sortable: true, width: "50%" },
];

const itemsPerPage = [
  { value: 25, title: "25" },
  { value: 50, title: "50" },
  { value: 100, title: "100" },
  { value: -1, title: "All" },
];

const typeFilterOptions = computed(() => {
  const types = new Set(itemArray.value.map((item) => item.typeDisplay));
  return ["All", ...Array.from(types).sort()];
});

const filteredItems = computed(() => {
  let items = itemArray.value;
  if (selectedTypeFilter.value[0] !== "All") {
    items = items.filter((item) =>
      selectedTypeFilter.value.includes(item.typeDisplay)
    );
  }
  if (showSelectedOnly.value) {
    items = items.filter((item) => selected.value.includes(item.id));
  }
  return items;
});

// Compute which selected items are user-picked vs auto-dependencies
const userSelectedSet = computed(() => {
  const set = new Set();
  for (const id of selected.value) {
    const item = itemArray.value.find((i) => i.id === id);
    if (item) {
      set.add(item.type + "|" + item.name);
    }
  }
  return set;
});

onMounted(async () => {
  await getData();
});

async function getData() {
  loading.value = true;

  let products = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getProducts",
  });

  if (Array.isArray(products)) {
    products.forEach((product) => {
      product.id = Math.floor(Math.random() * 10000000000000000000);
      productsArray.value.push(product);
    });
  }

  let items = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getItems",
  });

  if (Array.isArray(items)) {
    items.forEach((item) => {
      item.id = Math.floor(Math.random() * 10000000000000000000);
      item.typeDisplay = objectTypeDisplayNames[item.type] || item.type;
      itemArray.value.push(item);
    });
  }

  loading.value = false;
}

async function getPortalUser() {
  try {
    const session = await fetchAuthSession();
    return session.tokens.idToken.payload.email;
  } catch {
    return "Unknown";
  }
}

function clearProductObj() {
  productObj.value.existingProductId = "";
  productObj.value.productName = "";
  productObj.value.productDescription = "";
}

function nextStep() {
  if (currentStep.value < steps.length) {
    steps[currentStep.value - 1].complete = true;

    switch (currentStep.value) {
      case 1:
        if (selectedProduct.value.length === 0) {
          showWarningDialog.value = true;
          return;
        }
        // Load existing product details and pre-select its templates
        getProductDetails();
        preselectProductItems();
        currentStep.value++;
        break;
      case 2:
        if (selected.value.length === 0) {
          showWarningDialog.value = true;
          return;
        }
        // Resolve dependencies
        resolveDependencies();
        currentStep.value++;
        break;
      default:
        break;
    }
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    steps[currentStep.value - 1].complete = false;
  }
  if (currentStep.value === 1) {
    clearProductObj();
    selected.value = [];
    selectedProduct.value = [];
    resolvedItems.value = {};
  }
}

function getProductDetails() {
  if (selectedProduct.value.length > 0) {
    var id = selectedProduct.value[0];
    const product = productsArray.value.find((p) => p.id === id);
    if (product) {
      productObj.value.existingProductId = product._id;
      productObj.value.productName = product.name;
      productObj.value.productDescription = product.description || "";
    }
  }
}

function preselectProductItems() {
  if (selectedProduct.value.length === 0) return;

  var id = selectedProduct.value[0];
  const product = productsArray.value.find((p) => p.id === id);
  if (!product || !product.items) return;

  // Match product item names to itemArray entries
  for (const [type, names] of Object.entries(product.items)) {
    for (const name of names) {
      const match = itemArray.value.find(
        (item) => item.name === name && item.type === type
      );
      if (match && !selected.value.includes(match.id)) {
        selected.value.push(match.id);
      }
    }
  }
}

async function resolveDependencies() {
  resolving.value = true;

  // Build array of selected items
  const selectedItems = selected.value
    .map((id) => {
      const item = itemArray.value.find((i) => i.id === id);
      if (item) {
        return { name: item.name, type: item.type };
      }
    })
    .filter(Boolean);

  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "resolveProductDependencies",
    payload: selectedItems,
  });

  if (result && typeof result === "object") {
    resolvedItems.value = result;
  }

  resolving.value = false;
}

function createNewProduct() {
  showCreationDialog.value = false;
  // Set a fake selection to allow proceeding
  selectedProduct.value = ["new"];
  productObj.value.existingProductId = "";
  currentStep.value = 2;
  steps[0].complete = true;
}

async function submitProduct() {
  showSubmissionDialog.value = false;

  const portalUser = await getPortalUser();

  const payload = {
    _id: productObj.value.existingProductId,
    name: productObj.value.productName,
    description: productObj.value.productDescription,
    items: resolvedItems.value,
    latestEditor: portalUser,
    author: portalUser,
    type: productObj.value.existingProductId ? "existing" : "new",
  };

  try {
    const result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "editProduct",
      payload: payload,
    });

    if (result === "Success") {
      showCompletionDialog.value = true;
    } else {
      showCompletionWarningDialog.value = true;
    }
  } catch (error) {
    console.log(error);
    showCompletionWarningDialog.value = true;
  }
}

async function deleteSelectedProduct() {
  showDeleteDialog.value = false;

  var id = selectedProduct.value[0];
  const product = productsArray.value.find((p) => p.id === id);

  try {
    const result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "deleteProduct",
      payload: product,
    });

    if (result === "Success") {
      const index = productsArray.value.indexOf(product);
      if (index > -1) {
        productsArray.value.splice(index, 1);
      }
      showDeleteSuccessDialog.value = true;
    } else {
      showDeleteFailedDialog.value = true;
    }
  } catch (error) {
    console.log(error);
    showDeleteFailedDialog.value = true;
  }

  clearProductObj();
  selected.value = [];
  selectedProduct.value = [];
}

function resetWizard() {
  showCompletionDialog.value = false;
  currentStep.value = 1;
  productsArray.value = [];
  itemArray.value = [];
  steps.forEach((step) => {
    step.complete = false;
  });
  clearProductObj();
  search.value = "";
  selected.value = [];
  selectedProduct.value = [];
  resolvedItems.value = {};
  getData();
}

// Get sorted type keys for display
const resolvedTypeKeys = computed(() => {
  return Object.keys(resolvedItems.value).sort();
});
</script>

<template>
  <v-stepper v-model="currentStep">
    <v-stepper-header>
      <v-stepper-item
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :value="step.value"
        :complete="step.complete"
      ></v-stepper-item>
    </v-stepper-header>
    <v-stepper-content
      v-for="(step, index) in steps"
      :key="index"
      :step="step.value"
    >
      <v-sheet v-if="step.value === currentStep">
        <v-card-text>
          <!-- Step 1: Select Product -->
          <template v-if="step.value === 1">
            <h2>Select a product to edit, delete, or create a new one</h2>
            <v-responsive>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                variant="underlined"
                class="input-field"
                style="max-width: 478px"
              ></v-text-field>
            </v-responsive>
            <v-data-table
              :headers="productHeaders"
              :items="productsArray"
              :search="search"
              item-key="id"
              show-select
              v-model="selectedProduct"
              class="elevation-2"
              show-expand
              select-strategy="single"
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
              no-data-text="No products available"
            >
              <template v-slot:expanded-row="{ columns, item }">
                <tr>
                  <td :colspan="columns.length">
                    <table class="expanded-row">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Templates</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template
                          v-for="(names, type) in item.items"
                          :key="type"
                        >
                          <tr v-if="names && names.length > 0">
                            <td>
                              {{ objectTypeDisplayNames[type] || type }}
                            </td>
                            <td>{{ names.join(", ") }}</td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </template>

          <!-- Step 2: Select Templates -->
          <template v-if="step.value === 2">
            <h2>
              {{
                productObj.existingProductId
                  ? "Editing Product - " + productObj.productName
                  : "New Product - Select Templates"
              }}
            </h2>
            <v-responsive>
              <div class="d-flex align-center">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 20px"
                ></v-text-field>
                <v-select
                  v-model="selectedTypeFilter"
                  :items="typeFilterOptions"
                  chips
                  multiple
                  label="Type"
                  variant="underlined"
                  class="input-field"
                  style="max-width: 300px; margin-right: 20px"
                ></v-select>
                <v-switch
                  v-model="showSelectedOnly"
                  color="primary"
                  hide-details
                  density="compact"
                >
                  <template #label>
                    Selected
                    <v-chip size="small" class="ml-2" color="primary">
                      {{ selected.length }}
                    </v-chip>
                  </template>
                </v-switch>
              </div>
            </v-responsive>
            <v-data-table
              :headers="itemHeaders"
              :items="filteredItems"
              :search="search"
              item-key="id"
              show-select
              v-model="selected"
              class="elevation-2"
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
            ></v-data-table>
          </template>

          <!-- Step 3: Review & Save -->
          <template v-if="step.value === 3">
            <h2>Review Product</h2>

            <v-progress-linear
              v-if="resolving"
              indeterminate
              color="primary"
              class="mb-4"
            ></v-progress-linear>

            <template v-if="!resolving">
              <v-row class="mt-2">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="productObj.productName"
                    label="Product Name"
                    variant="outlined"
                    :disabled="!!productObj.existingProductId"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="productObj.productDescription"
                    label="Description"
                    variant="outlined"
                  ></v-text-field>
                </v-col>
              </v-row>

              <template v-for="type in resolvedTypeKeys" :key="type">
                <h3 class="mt-4 mb-2">
                  {{ objectTypeDisplayNames[type] || type }}
                </h3>
                <v-table density="compact" class="elevation-1 mb-2">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th style="width: 120px">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="name in resolvedItems[type]" :key="type + name">
                      <td>{{ name }}</td>
                      <td>
                        <v-chip
                          size="x-small"
                          :color="
                            userSelectedSet.has(type + '|' + name)
                              ? 'primary'
                              : 'orange'
                          "
                        >
                          {{
                            userSelectedSet.has(type + "|" + name)
                              ? "Selected"
                              : "Dependency"
                          }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
            </template>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="selectedProduct.length > 0 && currentStep === 1"
            variant="elevated"
            color="primary"
            @click="showDeleteDialog = true"
            class="mr-auto"
          >
            Delete Product
          </v-btn>
          <v-btn
            v-if="selectedProduct.length === 0 && currentStep === 1"
            variant="elevated"
            color="primary"
            @click="showCreationDialog = true"
            class="mr-auto"
          >
            Create New Product
          </v-btn>
          <v-btn
            v-if="currentStep > 1"
            variant="elevated"
            @click="prevStep"
            class="mr-auto"
            color="rgb(247, 247, 247)"
          >
            Back
          </v-btn>
          <v-btn
            v-if="currentStep < 3"
            variant="elevated"
            color="primary"
            @click="nextStep"
          >
            Next
          </v-btn>
          <v-btn
            v-if="currentStep === 3"
            @click="showSubmissionDialog = true"
            variant="elevated"
            color="primary"
            :disabled="!productObj.productName || resolving"
          >
            Save Product
          </v-btn>
        </v-card-actions>
      </v-sheet>

      <!-- Dialogs -->
      <v-dialog v-model="showCreationDialog" persistent>
        <v-card min-width="600" variant="elevated" class="mx-auto">
          <v-card-title>Enter the new Product details</v-card-title>
          <v-card-text>
            <v-table class="text-left">
              <v-responsive class="mx-auto py-2" max-width="600">
                <v-text-field
                  clearable
                  v-model="productObj.productName"
                  label="Product Name"
                  type="input"
                  hint="Enter the new product name"
                  variant="outlined"
                ></v-text-field>
              </v-responsive>
              <v-responsive class="mx-auto py-2" max-width="600">
                <v-text-field
                  clearable
                  v-model="productObj.productDescription"
                  label="Description"
                  type="input"
                  hint="Enter a description for the product"
                  variant="outlined"
                ></v-text-field>
              </v-responsive>
              <div class="text-center">
                <v-card>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn @click="showCreationDialog = false">Cancel</v-btn>
                    <v-btn
                      @click="createNewProduct"
                      class="flex-grow-1"
                      variant="tonal"
                      :disabled="
                        !productObj.productName ||
                        !productObj.productDescription
                      "
                    >
                      Create
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </div>
            </v-table>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showWarningDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>Please select an item before proceeding</h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="showWarningDialog = false"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>You are about to delete this product, please confirm</h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn @click="showDeleteDialog = false">Cancel</v-btn>
              <v-btn
                @click="deleteSelectedProduct"
                class="flex-grow-1"
                variant="tonal"
              >
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showDeleteSuccessDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>Product has been deleted</h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="showDeleteSuccessDialog = false"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showDeleteFailedDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>Product has failed to be deleted</h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="showDeleteFailedDialog = false"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showSubmissionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>
                You are about to save this product to staging. Are you sure you
                want to proceed?
              </h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn @click="showSubmissionDialog = false">Cancel</v-btn>
              <v-btn @click="submitProduct" class="flex-grow-1" variant="tonal">
                Proceed
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showCompletionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="success"
                icon="mdi-check-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">
                Product Saved to Staging
              </div>
              <div class="text-body-1 mt-2 px-4">
                Your product has been saved to staging. Visit the Jamf Staged
                page to request promotion to production.
              </div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="resetWizard"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog
        v-model="showCompletionWarningDialog"
        max-width="500"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="red"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Error Saving Product</div>
            </div>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showCompletionWarningDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="loading" persistent width="auto">
        <v-card color="primary">
          <v-card-text>
            Loading data, please wait...
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-stepper-content>
  </v-stepper>
</template>

<style scoped>
.input-field {
  width: 50%;
}
.expanded-row {
  width: 100%;
  border-collapse: collapse;
}
.expanded-row th {
  background-color: rgb(210, 210, 210);
  border: 1px solid #a4a4a4;
}
.expanded-row td {
  padding: 1px;
  border: 1px solid #a4a4a4;
}
.expanded-row td:nth-child(1) {
  width: 30%;
}
.expanded-row td:nth-child(2) {
  width: 70%;
}
</style>
